import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Form, Label } from "reactstrap";
import {
  IAddUpdateProductCityLevelProps,
  IProductCityLevelPayload,
  IProductCategory,
  IProductCityLevelInventoryPayload,
  IProductCityLevelInventory,
  IOdooTaxState,
  IOdooTaxListResponse,
  IWarehousePayload,
  INormalizedProductInventory,
  ICustomerChannelAttributesMap,
  IStorageBlock,
  Vertical,
} from "../../../interfaces/Index";
import {
  normalizeProductCityLevelInventoryUpdateReqPayload,
  normalizeWarehouseNameAndProductCityLevelEntry,
} from "../../../normalizer/ProductCityLevelNormalizer";
import FormSectionLayout from "../../layouts/FormSectionLayout";
import TextField from "../../common/TextField/TextField";
import ButtonComponent from "../../common/Button/ButtonComponent";
import HeadingText from "../../common/HeadingText/HeadingText";
import Switch from "../../common/Switch/Switch";
import LoaderComponent from "../../common/Loader/Loader";
import { numberOnlyValidator } from "../../../utils/Validators";

import backarrow from "../../../assets/images/back_arrow.svg";
import { AbilityContext } from "../../../config/Can";
import "./AddUpdateProductCityLevelPage.scss";
import {
  ODOO_TAX_TYPE,
  STORAGE_BLOCK_OPTIONS,
} from "../../../config/constants";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import { fetchProductSalesUomApiCall } from "../../../redux/sources/ProductCityLevelSource";
import EditableTable from "components/common/EditableTable/EditableTable";
import { ALLOWED_ROLE } from "./ProductCityLevelTableConstants";
import { handleStockChange } from "domain/productCityLevel/inventoryDepletion";
import _ from "lodash";

export const getProductUomSale = async (uomId: string) => {
  const uom: any = await fetchProductSalesUomApiCall(uomId);
  return uom ? uom[0].name : "";
};

const AddUpdateProductCityLevelPage: React.FunctionComponent<
  IAddUpdateProductCityLevelProps
> = (props: IAddUpdateProductCityLevelProps) => {
  const ability = useContext(AbilityContext);
  const [productState, setProductCityLevelState] =
    useState<IProductCityLevelPayload>({
      id: props.productCityLevel ? props.productCityLevel.id || "" : "",
      productCityLevelSku: props.productCityLevel
        ? props.productCityLevel.productCityLevelSku || ""
        : "",
      productCityLevelTitle: props.productCityLevel
        ? props.productCityLevel.productCityLevelTitle || ""
        : "",
      productCityLevelDescription: props.productCityLevel
        ? props.productCityLevel.productCityLevelDescription || ""
        : "",
      productCityLevelCategories: props.productCityLevel
        ? props.productCityLevel.productCityLevelCategories || ""
        : "",
      sellingTax: props.productCityLevel
        ? props.productCityLevel.sellingTax
        : "",
      purchaseTax: props.productCityLevel
        ? props.productCityLevel.purchaseTax
        : "",
      productCityLevelOriginalPrice: props.productCityLevel
        ? props.productCityLevel.productCityLevelOriginalPrice?.toString() || ""
        : "",
      productCityLevelDisplayPrice: props.productCityLevel
        ? props.productCityLevel.productCityLevelDisplayPrice?.toString() || ""
        : "",
      productCityLevelRetailPrice: props.productCityLevel
        ? props.productCityLevel.productCityLevelRetailPrice || ""
        : "",
      productCityLevelImage: props.productCityLevel
        ? props.productCityLevel.productCityLevelImage || ""
        : "",
      status: props.productCityLevel
        ? props.productCityLevel.status === "ACTIVE"
          ? true
          : false
        : false,
      variantId: props.productCityLevel
        ? props.productCityLevel.variantId || ""
        : "",
      selectedCity: props.selectedCity && props.selectedCity,
      uomSale: props.productCityLevel
        ? props.productCityLevel.uomSale || ""
        : "",
      appChannels: props.productCityLevel
        ? props.productCityLevel.appChannels || []
        : [],
      customerChannelAttributesMap: props.productCityLevel
        ? props.productCityLevel.customerChannelAttributesMap
        : {},
      advanceTaxApplicable: props.productCityLevel
        ? props.productCityLevel.advanceTaxApplicable || false
        : false,
      furtherTaxApplicable: props.productCityLevel
        ? props.productCityLevel.furtherTaxApplicable || false
        : false,
      storageBlock: props.productCityLevel.storageBlock,
    });
  const [odooTaxType, setOdooTaxType] = useState<IOdooTaxState>({
    sellingTax: [],
    purchaseTax: [],
  });

  const productCityLevelUpdated = useRef(false);
  const productCityLevelInventoryUpdated = useRef(false);

  const [storageBlockList, setStorageBlockList] = useState<IStorageBlock[]>(
    STORAGE_BLOCK_OPTIONS
  );

  const [taxBooleanState, setTaxBooleanState] = useState<boolean>(false);

  const generateInventoryItems = (data: IProductCityLevelInventory[]) => {
    return data.map((item: IProductCityLevelInventory) => {
      return {
        warehouseId: item.warehouseId,
        quantity: item.quantity || 0,
        primeQuantity: item.primeQuantity || 0,
      };
    });
  };

  const [
    warehouseProductCityLevelInventory,
    setWarehouseProductCityLevelInventory,
  ] = useState<IProductCityLevelInventoryPayload>({
    productCityLevelId: props.productCityLevel
      ? props.productCityLevel.id || ""
      : "",
    inventoryDetails: generateInventoryItems(
      props.productCityLevelInventory && props.productCityLevelInventory
        ? props.productCityLevelInventory.sort((a, b) =>
            a.warehouseId > b.warehouseId ? 1 : -1
          )
        : []
    ),
  });

  useEffect(() => {
    const { productCityLevelFetchTaxFromOdoo } = props;
    productCityLevelFetchTaxFromOdoo();
  }, []);

  useEffect(() => {
    const { isEdit } = props;
    if (isEdit) {
      const getUomSale = async () => {
        const uom = await getProductUomSale(uomSale);
        setProductCityLevelState({
          ...productState,
          uomSale: uom,
        });
        setTaxBooleanState(true);
      };
      getUomSale();
    }
  }, []);

  useEffect(() => {
    const {
      productCityLevel,
      isEdit,
      productCityLevelFetchWareHouseNames,
      fetchProductCityLevelInventory,
      selectedCity,
    } = props;
    if (isEdit && productCityLevel && productCityLevel.id) {
      productCityLevelFetchWareHouseNames({
        cityId: selectedCity.id,
      });
      fetchProductCityLevelInventory(productCityLevel.id as string, {
        cityId: selectedCity.id,
      });
    }
  }, [
    props.productCityLevelFetchWareHouseNames,
    props.fetchProductCityLevelInventory,
    props.productCityLevelDetails,
  ]);

  useEffect(() => {
    if (
      props.isProductCityLevelInventoryFetched &&
      props.productCityLevelInventory.length &&
      props.wareHouseNames
    ) {
      const inventoryDetailNormalized =
        normalizeWarehouseNameAndProductCityLevelEntry(
          props.wareHouseNames,
          props.productCityLevelInventory
        );
      setWarehouseProductCityLevelInventory({
        ...warehouseProductCityLevelInventory,
        inventoryDetails: inventoryDetailNormalized.sort((a, b) =>
          a.warehouseId > b.warehouseId ? 1 : -1
        ),
      });
    }
  }, [
    props.isProductCityLevelInventoryFetched,
    props.productCityLevelInventory,
    props.wareHouseNames,
  ]);

  useEffect(() => {
    const { odooTax, isProductCityLevelDetailsFetched } = props;
    if (odooTax && odooTax.length > 0) {
      setOdooTaxType({
        sellingTax: odooTax.filter((tax) => tax.taxType === ODOO_TAX_TYPE.SALE),
        purchaseTax: odooTax.filter(
          (tax) => tax.taxType === ODOO_TAX_TYPE.PURCHASE
        ),
      });
    }
    if (odooTax && odooTax.length > 0 && isProductCityLevelDetailsFetched) {
      const extractSellingTax = productCityLevelDetails.sellingTax
        ? extractDataField(
            productCityLevelDetails.sellingTax as string,
            odooTax as IOdooTaxListResponse[]
          )
        : "";

      const extractPurchaseTax = productCityLevelDetails.purchaseTax
        ? extractDataField(
            productCityLevelDetails.purchaseTax as string,
            odooTax as IOdooTaxListResponse[]
          )
        : "";

      if (extractSellingTax) {
        productCityLevelDetails.sellingTax = {
          id: extractSellingTax?.id as string,
          label: extractSellingTax?.label as string,
          value: extractSellingTax?.value as string,
        };
      }
      if (extractPurchaseTax) {
        productCityLevelDetails.purchaseTax = {
          id: extractPurchaseTax?.id as string,
          label: extractPurchaseTax?.label as string,
          value: extractPurchaseTax?.value as string,
        };
      }

      const combObj = {
        ...productState,
        ...productCityLevelDetails,
        uomSale: uomSale,
        productCityLevelCategories: productCityLevelCategories,
      };
      setProductCityLevelState(combObj);
    }
  }, [
    taxBooleanState &&
      props.isProductCityLevelDetailsFetched &&
      props.isOdooTaxFetched,
  ]);

  useEffect(() => {
    const { error, isError, commandoCategoryError } = props;
    if (isError) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    const {
      isProductCityLevelAdded,
      backButtonHandler,
      isProductCityLevelUpdated,
      isProductCityLevelInventoryFetched,
    } = props;
    if (isProductCityLevelAdded) {
      backButtonHandler(true, props.selectedCity);
    } else if (
      isProductCityLevelUpdated &&
      isProductCityLevelInventoryFetched
    ) {
      backButtonHandler(true, props.selectedCity);
    }
  }, [
    props.isProductCityLevelAdded,
    props.isProductCityLevelUpdated,
    props.isProductCityLevelInventoryFetched,
  ]);

  useEffect(() => {
    // all good here
    const { isProductCityLevelDetailsFetched, productCityLevelDetails } = props;
    if (isProductCityLevelDetailsFetched) {
      const combObj = {
        ...productState,
        ...productCityLevelDetails,
        uomSale,
        productCityLevelCategories,
      };

      setProductCityLevelState(combObj);
    }
  }, [props.isProductCityLevelDetailsFetched]);

  const extractDataField = (
    id: string,
    dataList: IProductCategory[] | IOdooTaxListResponse[]
  ) => {
    let value = dataList.find((item) => item.id === id);
    return value;
  };

  // rest of the functions
  const {
    productCityLevelSku,
    productCityLevelTitle,
    productCityLevelDescription,
    productCityLevelRetailPrice,
    productCityLevelImage,
    appChannels,
    // channels,
    sellingTax,
    purchaseTax,
    uomSale,
    productCityLevelCategories,
    storageBlock,
  } = productState;
  const {
    isEdit,
    productCityLevel,
    backButtonHandler,
    clearProductCityLevelDetails,
    isLoading,
    productCityLevelDetails,
    updateProductCityLevelInventory,
    roles,
  } = props;
  const { inventoryDetails } = warehouseProductCityLevelInventory;

  const headingText = isEdit
    ? productCityLevelDetails && productCityLevelDetails.productCityLevelSku
      ? `${productCityLevelDetails.productCityLevelSku}`
      : ""
    : "Add a product";

  const buttonText = isEdit ? "Update product" : "Add a product";

  const handleGoBackButton = () => {
    clearProductCityLevelDetails();
    backButtonHandler(false, props.selectedCity);
  };

  const handleChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    productCityLevelUpdated.current = true;
    setProductCityLevelState({
      ...productState,
      [fieldId]: fieldId === "status" ? !productState[fieldId] : fieldValue,
    });
  };

  const handleAdvanceTaxChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    productCityLevelUpdated.current = true;
    setProductCityLevelState({
      ...productState,
      [fieldId]:
        fieldId === "advanceTaxApplicable"
          ? !productState[fieldId]
          : fieldValue,
    });
  };

  const handleFurtherTaxChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    productCityLevelUpdated.current = true;
    setProductCityLevelState({
      ...productState,
      [fieldId]:
        fieldId === "furtherTaxApplicable"
          ? !productState[fieldId]
          : fieldValue,
    });
  };

  const updateInventoryDetails = (inventoryItem: any) => {
    productCityLevelInventoryUpdated.current = true;
    setWarehouseProductCityLevelInventory({
      ...warehouseProductCityLevelInventory,
      inventoryDetails: inventoryItem.slice(),
    });
  };

  useEffect(() => {
    return () => {
      clearProductCityLevelDetails();
    };
  }, []);

  const handleSubmit = (e: any) => {
    if (isEdit) {
      if (productCityLevelInventoryUpdated.current) {
        const reqProductCityLevelInventoryPayload =
          normalizeProductCityLevelInventoryUpdateReqPayload(
            warehouseProductCityLevelInventory
          );
        updateProductCityLevelInventory(
          productCityLevel.id as any,
          reqProductCityLevelInventoryPayload
        );
      }
    }
  };

  const validateForm = props.dummyValidations || (purchaseTax && sellingTax);
  const handleChannel = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    if (fieldId === "status") {
      productCityLevelUpdated.current = true;
      setProductCityLevelState({
        ...productState,
        [fieldId]: fieldId === "status" ? !productState[fieldId] : fieldValue,
      });
    } else {
      let channelData = [...appChannels, fieldValue];
      if (appChannels.includes(fieldValue)) {
        channelData = channelData.filter((val) => val !== fieldValue);
      }
      productCityLevelUpdated.current = true;
      setProductCityLevelState({
        ...productState,
        appChannels: channelData,
      });
    }
  };

  const channelStatus = (val: any) => {
    if (productState.appChannels?.includes(val)) {
      return true;
    } else {
      return false;
    }
  };

  const getWarehouseQuantity = (
    wareHouseData: IWarehousePayload,
    inventoryData: INormalizedProductInventory[],
    vertical: Vertical
  ) => {
    for (let i = 0; i < inventoryData.length; i++) {
      if (inventoryData[i].warehouseId == wareHouseData.id) {
        return vertical === Vertical.MARKETPLACE
          ? inventoryData[i].quantity
          : inventoryData[i].primeQuantity;
      }
    }
  };

  const productBasicDetails = () => {
    return (
      <FormSectionLayout subTitleText="" image={productCityLevelImage}>
        <div className="product-basic-details">
          <div className="details">
            <Label className="label">{productCityLevelSku}</Label>
            <HeadingText
              isMainHeading={false}
              text={productCityLevelTitle}
            ></HeadingText>
            <Label className="label">
              {uomSale} - {productCityLevelCategories}
            </Label>
          </div>
        </div>
      </FormSectionLayout>
    );
  };

  const renderInventoryCaps = () => {
    const { wareHouseNames } = props;
    return (
      <>
        <FormSectionLayout subTitleText="Inventory caps">
          {wareHouseNames.length &&
            wareHouseNames.map((warehouse: any, i: any) => (
              <>
                <TextField
                  id={warehouse.id}
                  className="warehouse-field"
                  label={warehouse.name}
                  placeholder="Stock"
                  value={getWarehouseQuantity(
                    warehouse,
                    inventoryDetails,
                    Vertical.MARKETPLACE
                  )}
                  handleChange={(e) => {
                    updateInventoryDetails(
                      handleStockChange(
                        warehouse.id,
                        e.target.value,
                        i,
                        warehouseProductCityLevelInventory,
                        Vertical.MARKETPLACE
                      )
                    );
                  }}
                  type="number"
                  onWheel={(e: any) => e.currentTarget.blur()}
                  disabled={
                    [
                      roles?.includes("Commando God"),
                      roles?.includes("Caps Upload"),
                      // roles?.includes("Commerical Supervisor"),
                    ].filter(Boolean).length === 0
                  }
                />
                <TextField
                  id={`${warehouse.id}-prime`}
                  label={`${warehouse.name} Prime`}
                  placeholder="Prime Stock"
                  value={getWarehouseQuantity(
                    warehouse,
                    inventoryDetails,
                    Vertical.PRIME
                  )}
                  handleChange={(e) => {
                    updateInventoryDetails(
                      handleStockChange(
                        warehouse.id,
                        e.target.value,
                        i,
                        warehouseProductCityLevelInventory,
                        Vertical.PRIME
                      )
                    );
                  }}
                  type="number"
                  onWheel={(e: any) => e.currentTarget.blur()}
                  disabled={
                    [
                      roles?.includes("Commando God"),
                      roles?.includes("Caps Upload"),
                    ].filter(Boolean).length === 0
                  }
                />
              </>
            ))}
        </FormSectionLayout>
      </>
    );
  };

  const updateGrid = (updatedPricing: ICustomerChannelAttributesMap) => {
    productCityLevelUpdated.current = true;
    setProductCityLevelState({
      ...productState,
      customerChannelAttributesMap: updatedPricing,
    });
  };

  const renderProductCityLevelPricingGrid = () => {
    return (
      <FormSectionLayout subTitleText="Pricing">
        {productCityLevelDetails.customerChannelAttributesMap && (
          <EditableTable
            customerChannel={
              productCityLevelDetails.customerChannelAttributesMap
            }
            updateGrid={updateGrid}
            roles={roles}
          />
        )}
      </FormSectionLayout>
    );
  };

  const handleSearchableSelect = (e: any, fieldId: string) => {
    productCityLevelUpdated.current = true;
    setProductCityLevelState({
      ...productState,
      [fieldId]: e,
    });
  };

  const renderFormButton = () => {
    return (
      <div className="form-row-separator">
        <Row id="addEditButtonRow">
          <Col lg="6">
            {roles?.includes("Caps Upload") && (
              // (ability.can("update", "product") ||
              //   ability.can("create", "product"))
              <ButtonComponent
                buttonClass="large-button btn-primary"
                buttonText={buttonText}
                onClickHandler={handleSubmit}
                isDisable={
                  // props.isLoading ||
                  // !validateForm ||
                  // !(
                  //   productCityLevelInventoryUpdated.current ||
                  //   productCityLevelUpdated.current
                  // )
                  !roles?.includes("Caps Upload")
                }
              />
            )}
          </Col>
        </Row>
      </div>
    );
  };

  const renderProductCityLevelImageForm = () => {
    return (
      <FormSectionLayout subTitleText="Additional details">
        <TextField
          id="productCityLevelRetailPrice"
          label="Retail Price"
          placeholder="e.g. 170"
          value={productCityLevelRetailPrice}
          error={numberOnlyValidator(productCityLevelRetailPrice)}
          disabled={!roles?.includes("Commando God")}
          handleChange={handleChange}
        />
        <div className="form-row">
          <Col sm="6">
            <SearchableSelect
              id="sellingTax"
              isSearchable={true}
              options={odooTaxType.sellingTax}
              onChange={(e: any) => {
                handleSearchableSelect(e, "sellingTax");
              }}
              label="Selling Tax"
              placeholder="Select tax"
              selectedValue={sellingTax}
              isDisabled={!roles?.includes("Commando God")}
            />
          </Col>
          <Col sm="6">
            <SearchableSelect
              id="purchaseTax"
              isSearchable={true}
              options={odooTaxType.purchaseTax}
              onChange={(e: any) => {
                handleSearchableSelect(e, "purchaseTax");
              }}
              label="Purchase Tax"
              placeholder="Select tax"
              selectedValue={purchaseTax}
              isDisabled={!roles?.includes("Commando God")}
              // value={numberOnlyValidator}
            />
          </Col>
        </div>
        <label className="label block text-uppercase">Advance Tax</label>
        <Switch
          id="advanceTaxApplicable"
          label=""
          name="advanceTaxApplicable"
          checked={!!productState.advanceTaxApplicable}
          handleSwitch={(e) => {
            handleAdvanceTaxChange(e);
          }}
          statusVal1="APPLICABLE"
          statusVal2="NON APPLICABLE"
          value={
            productState.advanceTaxApplicable === false ? "INACTIVE" : "ACTIVE"
          }
          disabled={!roles?.includes("Commando God")}
        />
        <div className="further-tax-margin">
          <label className="label block text-uppercase ">Further Tax</label>
          <Switch
            id="furtherTaxApplicable"
            label=""
            name="furtherTaxApplicable"
            checked={!!productState.furtherTaxApplicable}
            handleSwitch={(e) => {
              handleFurtherTaxChange(e);
            }}
            statusVal1="APPLICABLE"
            statusVal2="NON APPLICABLE"
            value={
              productState.furtherTaxApplicable === false
                ? "INACTIVE"
                : "ACTIVE"
            }
            disabled={!roles?.includes("Commando God")}
          />
        </div>
        <div className="visibility-container">
          <TextField
            id="productCityLevelDescription"
            label="Description"
            placeholder="e.g. The smooooooothest aaata grains eva!"
            value={productCityLevelDescription}
            handleChange={handleChange}
            disabled={!roles?.includes("Commando God")}
            isOptional
          />

          <SearchableSelect
            id="storageBlock"
            isSearchable={false}
            options={storageBlockList}
            onChange={(e: any) => {
              handleSearchableSelect(e, "storageBlock");
            }}
            label="Storage Block"
            placeholder="Select Storage"
            selectedValue={storageBlock}
            // value={numberOnlyValidator}
            isDisabled={!roles.includes(ALLOWED_ROLE)}
          />

          <label className="label block text-uppercase">Visibility</label>
          <Switch
            value="CUSTOMER_APP"
            id="customerApp"
            label=""
            checked={channelStatus("CUSTOMER_APP")}
            handleSwitch={handleChannel}
            statusVal1="Customer app"
            statusVal2="Customer app"
            disabled={
              !(
                roles?.includes("Commando God")
                // ||roles?.includes("Commercials") ||
                // roles?.includes("Commerical Supervisor")
              )
            }
          />

          <Switch
            value="AGENT_APP"
            id="agentApp"
            label=""
            checked={channelStatus("AGENT_APP")}
            handleSwitch={handleChannel}
            statusVal1="Agent app"
            statusVal2="Agent app"
            disabled={
              !(
                roles?.includes("Commando God")
                // ||roles?.includes("Commercials") ||
                // roles?.includes("Commerical Supervisor")
              )
            }
          />

          <Switch
            value="ADMIN_COMMANDO"
            id="adminCommando"
            label=""
            checked={channelStatus("ADMIN_COMMANDO")}
            handleSwitch={handleChannel}
            statusVal1="Commando"
            statusVal2="Commando"
            disabled={
              !(
                roles?.includes("Commando God")
                // || roles?.includes("Commercials") ||
                // roles?.includes("Commerical Supervisor")
              )
            }
          />
          <Switch
            value="KHAATA_APP"
            id="khaataApp"
            label=""
            checked={channelStatus("KHAATA_APP")}
            handleSwitch={handleChannel}
            statusVal1="Khaata app"
            statusVal2="Khaata app"
            disabled={
              !(
                roles?.includes("Commando God")
                // || roles?.includes("Commercials") ||
                // roles?.includes("Commerical Supervisor")
              )
            }
          />
        </div>
        {renderFormButton()}
      </FormSectionLayout>
    );
  };

  return (
    <div>
      <Container fluid>
        {isLoading && <LoaderComponent />}
        <Row>
          <Col>
            <div className="back-button">
              <a href="javascript:void(0)" onClick={handleGoBackButton}>
                {" "}
                {/* TO-DO: Convert this to a button in all compoents. */}
                <span>
                  <img src={backarrow} alt="close" />
                </span>{" "}
                Back to Products
              </a>
            </div>
            <HeadingText text="Product detail" isMainHeading={true} />
          </Col>
        </Row>
        <Row className="form-container add-update-product-page">
          <Col xs="12">
            <Form>
              <div className="form-row-separator">{productBasicDetails()}</div>
              <div className="form-row-separator">{renderInventoryCaps()}</div>
              {/* <div className="form-row-separator">
                {renderProductCityLevelPricingGrid()}
              </div>
              <div className="form-row-separator">
                {renderProductCityLevelImageForm()}
              </div> */}
              {renderFormButton()}
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

AddUpdateProductCityLevelPage.defaultProps = {
  dummyValidations: false,
};

export default AddUpdateProductCityLevelPage;
